import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCheck, Hand, CheckCircle2, Camera, Sparkles, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../hooks/useNotifications";
import { useOnline } from "../lib/net";

const ICON = { claimed: Hand, completed: CheckCircle2, proof_submitted: Camera, test: Sparkles };
const rel = (iso) => { const s = Math.max(0, (Date.now() - new Date(iso)) / 1000); if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s / 60)} min ago`; if (s < 86400) return `${Math.floor(s / 3600)} h ago`; return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); };
const urlOf = (n) => (n.dispatch_id ? `/app/jobs/${n.dispatch_id}` : "/app/settings");

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const { items, unread_count, mode, markRead } = useNotifications(user?.id, {
    onNew: (n) => toast(n.title, { description: n.body, action: n.dispatch_id ? { label: "Open", onClick: () => { markRead([n.id]); navigate(urlOf(n)); } } : undefined, duration: 8000 }),
  });
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey); document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);
  const toggle = (e) => { const r = e.currentTarget.getBoundingClientRect(); setAnchor({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) }); setOpen((o) => !o); };
  const openItem = (n) => { setOpen(false); if (!n.read_at) markRead([n.id]); navigate(urlOf(n)); };
  return (
    <div className="relative">
      <button type="button" className="icon-btn relative" onClick={toggle} aria-label={`Notifications${unread_count ? ` (${unread_count} unread)` : ""}`} data-testid="notifications-bell" data-mode={mode}>
        <Bell size={18} />
        {unread_count > 0 && <span className="bell-badge notranslate" translate="no" data-testid="notifications-unread-badge">{unread_count > 99 ? "99+" : unread_count}</span>}
      </button>
      {open && createPortal(
        <div className="notif-scrim" onClick={() => setOpen(false)} data-testid="notifications-scrim">
          <div className="notif-panel" role="dialog" aria-label="Alerts" style={anchor ? { "--anchor-top": `${anchor.top}px`, "--anchor-right": `${anchor.right}px` } : undefined} onClick={(e) => e.stopPropagation()} data-testid="notifications-panel">
            <div className="notif-grip" aria-hidden="true" />
            <div className="flex items-center justify-between px-4 pt-2 pb-2">
              <p className="eyebrow">Alerts</p>
              <div className="flex items-center gap-3">
                <button type="button" className="text-xs text-primary flex items-center gap-1 disabled:opacity-40" onClick={() => markRead("all")} disabled={!unread_count || !online} data-testid="notifications-mark-all"><CheckCheck size={14} /> Mark all read</button>
                <button type="button" className="icon-btn !w-8 !h-8" onClick={() => setOpen(false)} aria-label="Close" data-testid="notifications-close"><X size={14} /></button>
              </div>
            </div>
            {!online && <p className="text-[11px] text-muted px-4 pb-1" data-testid="notifications-offline">Offline — showing saved alerts</p>}
            <div className="notif-list">
              {items.length === 0 && <p className="text-sm text-muted px-4 py-8 text-center" data-testid="notifications-empty">No alerts yet. You'll hear when someone claims or completes a job.</p>}
              {items.map((n) => { const I = ICON[n.type] || Bell; return (
                <button type="button" key={n.id} className={`notif-item ${n.read_at ? "" : "unread"}`} onClick={() => openItem(n)} data-testid={`notification-${n.id}`} data-read={n.read_at ? "true" : "false"}>
                  <I size={16} className="shrink-0 mt-0.5 text-primary" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold notif-title notranslate" translate="no">{n.title}</span>
                    {n.body && <span className="block text-xs notif-muted notranslate" translate="no">{n.body}</span>}
                    <span className="block text-[11px] notif-muted mt-0.5 notranslate" translate="no">{rel(n.created_at)}</span>
                  </span>
                  {!n.read_at && <span className="unread-dot" data-testid="notification-unread-dot" />}
                </button>
              ); })}
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
};
